import { useState, useEffect, useContext } from 'react';
import axios from '../../axios-config';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  ChevronRight,
  ScrollText,
  Search,
  Plus,
  Timer,
  RefreshCw,
  Settings,
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import { useHallPassTypes } from '../../contexts/HallPassTypesContext';
import { startNotificationConnection } from '../../services/notificationHubService';
import { MaraudersMap } from './map/MaraudersMap';
import studentIncidentService from '../../services/studentIncidentService';
import { StudentIncident } from '../../types';
import HallPassTypesPage from '../../pages/HallPassTypesPage';
import UserHallPassTypesPage from '../../pages/UserHallPassTypesPage';
import { toast } from 'react-toastify';
import { hallPassApiService, MapPathUserAssignment } from '../../services/hallPassApiService';
import { useHallPasses } from '../../contexts/HallPassesContext';
import StudentHallPassHistory from './StudentHallPassHistory';

// Authorization: who can end a hall pass
const canEndHallPass = (pass: any, user: any) => {
  if (!pass || !user) return false;
  const role = user.mainRole || '';
  if (role.includes('CAMPUSSECURITYOFFICER')) return true; // CSO override
  if (role.includes('ACORNLEAD')) return true; // Acorn Lead override
  if (role.includes('TEACHER') && pass.teacherId && pass.teacherId === user.userId) return true; // issuing teacher
  return false;
};

export function HallPassView() {
  const navigate = useNavigate();
  const { userProfile, hasClaim } = useContext(UserContext);
  const students = userProfile?.students || [];
  const teachers = userProfile?.teachers || [];

  const [showSummonsModal, setShowSummonsModal] = useState(false);
  const [summonsForm, setSummonsForm] = useState({
    studentName: '',
    studentLocation: '',
    summonedBy: '',
    summonedTo: '',
    notes: ''
  });
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const [showTeacherSuggestions, setShowTeacherSuggestions] = useState(false);
  const [showIncidentSuggestions, setShowIncidentSuggestions] = useState(false);
  const [showAddIncident, setShowAddIncident] = useState(false);
  const [incidentForm, setIncidentForm] = useState({ studentName: '', incident: '' });
  const [selectedStudentDetail, setSelectedStudentDetail] = useState<any | null>(null);
  const [showStudentIncidentForm, setShowStudentIncidentForm] = useState(false);
  const [studentIncidentText, setStudentIncidentText] = useState('');
  const [showRefreshSettings, setShowRefreshSettings] = useState(false);
  const [showStudentHistory, setShowStudentHistory] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);

  // Store the selected hall pass TYPE by its stable ID (prevents string-mismatch resets)
  const [selectedDestination, setSelectedDestination] = useState<string | number | ''>('');
  const [showTimeframeModal, setShowTimeframeModal] = useState(false);
  const [timeframe, setTimeframe] = useState({
    type: 'today',
    label: 'Today',
    startDate: new Date(),
    startTime: '00:00',
    endDate: new Date(),
    endTime: '23:59'
  });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    activeHallPasses,
    hallPassHistory,
    applyHistoryFilter,
    upsertFromHub,
    removeFromHub,
    lastUpdated,
    autoRefreshEnabled,
    autoRefreshInterval,
    timeUntilNextRefresh,
    setAutoRefreshEnabled,
    setAutoRefreshInterval,
    forceRefresh,
    loading: hallPassLoading,
    currentTab,
    setCurrentTab,
  } = useHallPasses();

  const [incidents, setIncidents] = useState<StudentIncident[]>([]);
  const { hallPassTypes } = useHallPassTypes();

  // Teacher preference state (keep if used elsewhere)
  const [teacherPreferences, setTeacherPreferences] = useState<MapPathUserAssignment[]>([]);
  const [preferredHallPassTypes, setPreferredHallPassTypes] = useState<any[]>([]);

  // Minute tick to refresh timers
  const [, forceTick] = useState(0);

  // Minute tick
  useEffect(() => {
    const interval = setInterval(() => forceTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (userProfile?.mainRole === 'CAMPUSSECURITYOFFICER') {
      studentIncidentService.getAll().then(setIncidents).catch(() => {});
    }
  }, [userProfile?.mainRole]);

  useEffect(() => {
    if (!selectedDestination && hallPassTypes.length > 0) {
      if (preferredHallPassTypes.length > 0) {
        const defaultType = preferredHallPassTypes[0];
        if (defaultType?.hallPassTypeId) setSelectedDestination(defaultType.hallPassTypeId);
      } else {
        const defaultType = hallPassTypes.find(t => t.isDefault) ?? hallPassTypes[0];
        if (defaultType?.hallPassTypeId) setSelectedDestination(defaultType.hallPassTypeId);
      }
    }
  }, [hallPassTypes, selectedDestination, preferredHallPassTypes]);

  // Fetch teacher's own hall pass type preferences
  useEffect(() => {
    const fetchTeacherPreferences = async () => {
      if (!userProfile?.userId || !userProfile?.mainRole?.includes('TEACHER')) return;
      try {
        const prefs = await hallPassApiService.getUserPathAssignments(userProfile.userId);
        const sortedPrefs = [...prefs].sort((a, b) => a.sortOrder - b.sortOrder);
        setTeacherPreferences(sortedPrefs);
      } catch (err) {
        console.error('Failed to fetch teacher preferences:', err);
      }
    };
    fetchTeacherPreferences();
  }, [userProfile]);

  // Derive preferredHallPassTypes whenever teacherPreferences or hallPassTypes change
  useEffect(() => {
    if (teacherPreferences.length > 0) {
      const preferredTypes = teacherPreferences
        .map(pref => hallPassTypes.find(type => type.path?.pathId === pref.pathId))
        .filter(Boolean) as any[];
      const preferredTypeIds = preferredTypes.map(t => t?.hallPassTypeId);
      const remainingTypes = hallPassTypes.filter(t => !preferredTypeIds.includes(t.hallPassTypeId));
      setPreferredHallPassTypes([...preferredTypes, ...remainingTypes]);
    } else {
      setPreferredHallPassTypes(hallPassTypes);
    }
  }, [teacherPreferences, hallPassTypes]);

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleStudentSelect = (studentName: string) => {
    setSearchQuery(studentName);
    setShowSuggestions(false);
  };

  const handleEndPass = async (passId: string) => {
    const passToEnd = activeHallPasses.find(pass => pass.hallPassId === passId);
    if (passToEnd) {
      if (!canEndHallPass(passToEnd, userProfile)) {
        toast.error('You do not have permission to end this hall pass.');
        return;
      }
    }
    try {
      await axios.put(`/api/HallPasses/ReturnHallPass/${passId}`);
      if (passToEnd) {
        removeFromHub(passToEnd);
        upsertFromHub({ ...passToEnd, status: 'returned', returnAt: new Date().toISOString(), endTime: new Date().toISOString() });
      } else {
        removeFromHub({ hallPassId: passId });
      }
    } catch (error) {
      console.error('Return hall pass error:', error);
      toast.error('Failed to end hall pass.');
    }
  };

  const filteredHistory = hallPassHistory
    .filter(pass => {
      const stud = students.find(s => s.userId === pass.studentId);
      return !searchTerm || (stud?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      const at = new Date(a.startTime || a.createdAt || 0).getTime();
      const bt = new Date(b.startTime || b.createdAt || 0).getTime();
      return bt - at;
    });

  const filteredIncidents = incidents.filter(inc => {
    const stud = students.find(s => s.userId === inc.userId);
    const query = searchTerm.toLowerCase();
    return (
      !searchTerm ||
      (stud?.name || '').toLowerCase().includes(query) ||
      (inc.incident || '').toLowerCase().includes(query)
    );
  });

  const filteredLookupStudents = students.filter(s =>
    !searchTerm || (s.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Summons validation
  const isSummonsInvalid = () =>
    !summonsForm.studentName.trim() ||
    !summonsForm.studentLocation.trim() ||
    !summonsForm.summonedBy.trim();

  // Send a new summons
  const handleSummonsSubmit = async () => {
    if (isSummonsInvalid()) return;

    const student = students.find(s => s.name === summonsForm.studentName);
    const teacher = teachers.find(s => s.name === summonsForm.studentLocation);
    if (!student || !teacher) return;

    const summonsType = preferredHallPassTypes.length > 0
      ? preferredHallPassTypes[0]
      : hallPassTypes.find(t => t.isDefault) ?? hallPassTypes[0];

    const payload = {
      studentId: student.userId,
      teacherId: teacher.id,
      organizationId: userProfile?.orgId,
      hallPassTypeId: summonsType?.hallPassTypeId,
      summonedBy: summonsForm.summonedBy,
      summonedTo: summonsForm.summonedTo,
      notes: summonsForm.notes
    };

    try {
      const { data: newHp } = await axios.post('/api/HallPasses', payload);
      setShowSummonsModal(false);
      upsertFromHub(newHp);
    } catch (error: any) {
      toast.error(error.response?.data || error.message, { autoClose: 25000 });
    }
  };

  const handleIncidentSave = async () => {
    const student = students.find(s => s.name === incidentForm.studentName);
    if (!student || !incidentForm.incident.trim()) return;
    try {
      const created = await studentIncidentService.create({
        userId: student.userId,
        incident: incidentForm.incident
      });
      setIncidents(prev => [...prev, created]);
      setShowAddIncident(false);
      setIncidentForm({ studentName: '', incident: '' });
    } catch {
      // ignore errors
    }
  };

  const handleStudentIncidentSave = async () => {
    if (!selectedStudentDetail || !studentIncidentText.trim()) return;
    try {
      const created = await studentIncidentService.create({
        userId: selectedStudentDetail.userId,
        incident: studentIncidentText
      });
      setIncidents(prev => [...prev, created]);
      setStudentIncidentText('');
      setShowStudentIncidentForm(false);
    } catch {
      // ignore errors
    }
  };

  // Track if we've loaded history data for this tab session to prevent infinite loops
  const [historyTabInitialized, setHistoryTabInitialized] = useState(false);
  
  // Auto-load history data when history tab is first accessed
  useEffect(() => {
    if (currentTab === 'history' && !historyTabInitialized && !hallPassLoading) {
      // Automatically apply the default "today" filter when history tab is first opened
      setHistoryTabInitialized(true);
      // Reset timeframe to "today" when returning to history tab
      setTimeframe({
        type: 'today',
        label: 'Today',
        startDate: new Date(),
        startTime: '00:00',
        endDate: new Date(),
        endTime: '23:59'
      });
      applyHistoryFilter({ range: 'today' });
    } else if (currentTab !== 'history') {
      // Reset the flag when leaving the history tab so it can auto-load again on return
      setHistoryTabInitialized(false);
    }
  }, [currentTab, historyTabInitialized, hallPassLoading]);

  // Auto-create a hall pass when both student & destination selected
  useEffect(() => {
    const student = students.find(s => s.name === searchQuery);
    const type = hallPassTypes.find(t => String(t.hallPassTypeId) === String(selectedDestination));
    if (student && type) {
      axios.post('/api/HallPasses', {
        studentId: student.userId,
        teacherId: userProfile?.userId,
        hallPassTypeId: type.hallPassTypeId,
        organizationId: userProfile?.orgId,
      })
      .then(res => {
        upsertFromHub(res.data);
      })
      .catch((e) => toast.error(e.response?.data || e.message, { autoClose: 25000 }));
      setSearchQuery('');
    }
  }, [searchQuery, selectedDestination, students, hallPassTypes]);

  // 🔔 SignalR sync
  const handleHubHallPass = (hp: any) => {
    upsertFromHub(hp);
  };

  useEffect(() => {
    let connection: any;
    (async () => {
      connection = await startNotificationConnection(handleHubHallPass, 'nonStudent');
    })();
    return () => {
      connection?.off?.('ReceiveHallPass');
      connection?.off?.('SummonsHallPass');
      connection?.off?.('ReturnHallPass');
      connection?.off?.('ExpiredHallPass');
      connection?.off?.('CancelledHallPass');
    };
  }, []);

  // OPEN summons dialog when requested from the sidebar
  useEffect(() => {
    const openHandler = () => setShowSummonsModal(true);
    window.addEventListener('openSummons', openHandler);
    return () => window.removeEventListener('openSummons', openHandler);
  }, []);

  // Cross-teacher updates via global events
  useEffect(() => {
    const handleCrossTeacherUpdate = (event: any) => {
      const { type, hallPass, originatingUserId } = event.detail;
      if (originatingUserId === userProfile?.userId) return;
      if (type === 'upsert') upsertFromHub(hallPass);
      else if (type === 'remove') removeFromHub(hallPass);
    };
    window.addEventListener('hallPassUpdated', handleCrossTeacherUpdate);
    return () => window.removeEventListener('hallPassUpdated', handleCrossTeacherUpdate);
  }, [userProfile?.userId, upsertFromHub, removeFromHub]);

  // Student history modal handlers
  const handleStudentHistoryClick = (studentId: string) => {
    setSelectedStudentId(studentId);
    setShowStudentHistory(true);
  };
  const handleBackFromStudentHistory = () => {
    setShowStudentHistory(false);
    setSelectedStudentId(null);
  };

  if (showStudentHistory && selectedStudentId) {
    return (
      <StudentHallPassHistory
        studentId={selectedStudentId}
        onBack={handleBackFromStudentHistory}
        hallPassHistory={hallPassHistory}
        students={students}
        teachers={teachers}
        hallPassTypes={hallPassTypes}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Optional top bar with “Create Summons” for teachers (hidden by default) */}
      {userProfile?.mainRole?.includes("TEACHER") && false ? (
        <div className="mb-6 flex items-center gap-4">
          <h1 className="text-2xl font-semibold">Hall Pass</h1>
          <button
            className="flex items-center gap-2 px-3 py-1.5 text-gray-700 border rounded-lg hover:bg-gray-50"
            onClick={() => setShowSummonsModal(true)}
          >
            <ScrollText className="w-4 h-4" />
            Create Summons
          </button>
        </div>
      ) : (
        <div />
      )}

      {/* Summons Modal */}
      {showSummonsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-[500px]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Create Student Summons</h3>
              <button
                onClick={() => setShowSummonsModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Student */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student Name<span className="text-red-500 ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="Start typing student's name or ID"
                    value={summonsForm.studentName}
                    onChange={(e) => {
                      setSummonsForm({ ...summonsForm, studentName: e.target.value });
                      setShowStudentSuggestions(true);
                    }}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  {showStudentSuggestions && summonsForm.studentName && (
                    <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto z-50">
                      {students
                        .filter(s => s.name.toLowerCase().includes(summonsForm.studentName.toLowerCase()))
                        .map(student => (
                          <button
                            key={student.userId}
                            onClick={() => {
                              setSummonsForm({ ...summonsForm, studentName: student.name });
                              setShowStudentSuggestions(false);
                            }}
                            className="flex items-center gap-3 w-full p-2 hover:bg-gray-50 text-left"
                          >
                            <img src={student.avatar} alt="" className="w-8 h-8 rounded-full" />
                            <div>
                              <p className="font-medium">{student.name}</p>
                              <p className="text-sm text-gray-500">{student.grade}</p>
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Current Classroom Teacher */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Classroom Teacher<span className="text-red-500 ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="Start typing teacher's name or subject"
                    value={summonsForm.studentLocation}
                    onChange={(e) => {
                      setSummonsForm({ ...summonsForm, studentLocation: e.target.value });
                      setShowTeacherSuggestions(true);
                    }}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  {showTeacherSuggestions && summonsForm.studentLocation && (
                    <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto z-50">
                      {teachers
                        .filter(s => s.name.toLowerCase().includes(summonsForm.studentLocation.toLowerCase()))
                        .map(teacher => (
                          <button
                            key={teacher.userId ?? teacher.id}
                            onClick={() => {
                              setSummonsForm({ ...summonsForm, studentLocation: teacher.name });
                              setShowTeacherSuggestions(false);
                            }}
                            className="flex items-center gap-3 w-full p-2 hover:bg-gray-50 text-left"
                          >
                            <img src={teacher.avatar} alt="" className="w-8 h-8 rounded-full" />
                            <div>
                              <p className="font-medium">{teacher.name}</p>
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Summoned By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Summoned By<span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Enter name or department"
                  value={summonsForm.summonedBy}
                  onChange={(e) => setSummonsForm({ ...summonsForm, summonedBy: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* Summoned To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summoned To (Location)</label>
                <input
                  type="text"
                  placeholder="Enter destination"
                  value={summonsForm.summonedTo}
                  onChange={(e) => setSummonsForm({ ...summonsForm, summonedTo: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  placeholder="Add any additional notes"
                  value={summonsForm.notes}
                  onChange={(e) => setSummonsForm({ ...summonsForm, notes: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg h-24 resize-none"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowSummonsModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSummonsSubmit}
                disabled={isSummonsInvalid()}
                className={`px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg ${isSummonsInvalid() ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Create Summons
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-refresh Settings Modal */}
      {showRefreshSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-[400px]">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Auto-refresh Settings</h3>
              <button
                onClick={() => setShowRefreshSettings(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Enable Auto-refresh</label>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefreshEnabled}
                    onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              {/* Refresh Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Refresh Interval</label>
                <select
                  value={autoRefreshInterval}
                  onChange={(e) => setAutoRefreshInterval(parseInt(e.target.value, 10))}
                  disabled={!autoRefreshEnabled}
                  className="w-full px-3 py-2 border rounded-lg disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value={15000}>15 seconds</option>
                  <option value={30000}>30 seconds</option>
                  <option value={60000}>1 minute</option>
                  <option value={120000}>2 minutes</option>
                  <option value={300000}>5 minutes</option>
                </select>
              </div>

              {/* Status Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>Auto-refresh: {autoRefreshEnabled ? 'Enabled' : 'Disabled'}</div>
                  <div>Interval: {autoRefreshInterval / 1000} seconds</div>
                  {lastUpdated && <div>Last updated: {lastUpdated.toLocaleString()}</div>}
                  {autoRefreshEnabled && timeUntilNextRefresh > 0 && (
                    <div>Next refresh in: {Math.ceil(timeUntilNextRefresh / 1000)} seconds</div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> Auto-refresh pauses when the browser tab is not active to save resources.
                  Real-time updates via SignalR will continue to work regardless of this setting.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowRefreshSettings(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Close
              </button>
              <button
                onClick={forceRefresh}
                className="px-4 py-2 text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
              >
                Refresh Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teacher quick create controls – only on Active tab */}
      {userProfile?.mainRole?.includes("TEACHER") && currentTab === 'active' && (
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
            <div className="relative">
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <div className="relative">
                <input
                  type="text"
                  placeholder="Start typing student's name or ID"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className="pl-10 pr-4 py-2 w-full border rounded-lg"
                />
                {showSuggestions && searchQuery && (
                  <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto z-50">
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map((student) => (
                        <button
                          key={student.userId}
                          onClick={() => handleStudentSelect(student.name)}
                          className="flex items-center gap-3 w-full p-2 hover:bg-gray-50 text-left"
                        >
                          <img src={student.avatar} alt={student.name} className="w-8 h-8 rounded-full" />
                          <div>
                            <p className="font-medium">{student.name}</p>
                            <p className="text-sm text-gray-500">{student.grade}</p>
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-2 text-gray-500">No students found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Type of Pass</label>
            <div className="relative">
              <select
                value={selectedDestination === '' ? '' : String(selectedDestination)}
                onChange={(e) => setSelectedDestination(e.target.value)}
                className="w-full pl-3 pr-10 py-2 text-base border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                {(preferredHallPassTypes.length > 0 ? preferredHallPassTypes : hallPassTypes).map((type) => (
                  <option key={type.hallPassTypeId} value={String(type.hallPassTypeId)}>
                    {`${type.hallPassTypeName ?? type.hallPassTypeDescription} (${type.hallPassTypeDuration} min)`}
                  </option>
                ))}
              </select>
              <ChevronRight
                aria-hidden="true"
                className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 transform rotate-90 pointer-events-none"
              />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="border-b mb-6">
          <div className="flex justify-between items-center">
            <div className="flex gap-4">
              <button
                onClick={() => setCurrentTab('active')}
                className={`px-4 py-2 font-medium ${currentTab === 'active' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                title={activeHallPasses.length === 0 ? 'No active passes currently' : 'View active passes'}
              >
                Active Hall Passes {activeHallPasses.length > 0 && (
                  <span className="ml-1 inline-block text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                    {activeHallPasses.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setCurrentTab('history')}
                className={`px-4 py-2 font-medium ${currentTab === 'history' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Hall Pass History
              </button>
              <button
                onClick={() => setCurrentTab('map')}
                className={`px-4 py-2 font-medium ${currentTab === 'map' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Map View
              </button>
              {hasClaim('CanManageHallPasses') && (
                <button
                  onClick={() => setCurrentTab('types')}
                  className={`px-4 py-2 font-medium ${currentTab === 'types' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  Types
                </button>
              )}
              {hasClaim('CanManageHallPasses') && (
                <button
                  onClick={() => setCurrentTab('userHallPassTypes')}
                  className={`px-4 py-2 font-medium ${currentTab === 'userHallPassTypes' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  User Pass Types
                </button>
              )}
            </div>

            {(currentTab === 'active' || currentTab === 'map') && (
              <div className="flex items-center gap-3">
                {lastUpdated && (
                  <span className="text-xs text-gray-500">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </span>
                )}
                {autoRefreshEnabled && timeUntilNextRefresh > 0 && (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Timer className="w-3 h-3" />
                    <span>{Math.ceil(timeUntilNextRefresh / 1000)}s</span>
                  </div>
                )}
                <button
                  onClick={forceRefresh}
                  disabled={hallPassLoading}
                  className={`p-2 rounded-lg transition-colors ${
                    hallPassLoading ? 'text-gray-400 bg-gray-50 cursor-not-allowed'
                                     : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                  }`}
                  title="Refresh hall pass data"
                >
                  <RefreshCw className={`w-4 h-4 ${hallPassLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowRefreshSettings(true)}
                  className="p-2 rounded-lg text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                  title="Auto-refresh settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* main content */}
        {currentTab === 'active' && activeHallPasses.length > 0 ? (
          <div className="space-y-4">
            {activeHallPasses.map((pass) => {
              const stud = students.find(s => s.userId === pass.studentId);
              const teacherForPass = teachers.find(t => t.userId === pass.teacherId || t.id === pass.teacherId) || pass.issuedBy;

              let parsedStart = new Date(pass.startTime);
              if (isNaN(parsedStart.getTime())) parsedStart = new Date();
              const duration = Number(pass.duration) || 5;
              const elapsedMs = Date.now() - parsedStart.getTime();
              const elapsedMinutes = elapsedMs <= 0 ? 0 : Math.floor(elapsedMs / 60000);
              const remainingMinutes = Math.max(0, duration - elapsedMinutes);
              const isOverdue = elapsedMinutes > duration;
              const overdueMinutes = isOverdue ? elapsedMinutes - duration : 0;

              return (
                <div key={pass.hallPassId ?? pass.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img src={stud?.avatar || ''} alt={stud?.name || pass.studentName || ''} className="w-10 h-10 rounded-full bg-gray-200" />
                    <div>
                      <p className="font-medium hover:text-indigo-600 cursor-pointer" onClick={() => navigate(`/hall-pass/student/${pass.id}`)}>
                        {stud?.name || pass.studentName || 'Unknown Student'}
                      </p>
                      <p className="text-sm text-gray-500">{teacherForPass?.name || 'Teacher Unknown'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <p className="font-medium">
                        {(() => {
                          const hallPassType = hallPassTypes.find(type => type.hallPassTypeId === pass.hallPassTypeId);
                          if (hallPassType?.startLocationDetails && hallPassType?.destinationLocationDetails) {
                            return `${hallPassType.startLocationDetails.name} → ${hallPassType.destinationLocationDetails.name}`;
                          }
                          if (pass.startLocation && pass.destinationLocation) {
                            return `${pass.startLocation.name} → ${pass.destinationLocation.name}`;
                          }
                          if (pass.location && pass.location !== 'Hall Pass') {
                            return pass.location;
                          }
                          return hallPassType?.hallPassTypeName || hallPassType?.hallPassTypeDescription || pass.hallPassTypeName || pass.hallPassTypeDescription || 'Hall Pass';
                        })()}
                      </p>
                      <p className="text-xs text-gray-400">{pass.location}</p>
                      <p className="text-sm text-gray-500">
                        Started at {parsedStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>

                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${isOverdue ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
                      <Timer className="h-4 w-4" />
                      <span className="text-sm">{isOverdue ? `${overdueMinutes}m overdue` : `${remainingMinutes}m remaining`}</span>
                    </div>

                    {pass.issuedBy && (
                      <div className="flex items-center gap-4">
                        <img src={pass.issuedBy.avatar} alt={pass.issuedBy.name} className="w-5 h-5 rounded-full" />
                        <span className="text-xs text-gray-500">from {pass.issuedBy.location} by {pass.issuedBy.name}</span>
                      </div>
                    )}

                    {canEndHallPass(pass, userProfile) && (
                      <button onClick={() => handleEndPass(pass.hallPassId)} className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg">
                        End Pass
                      </button>
                    )
                  }
                  </div>
                </div>
              );
            })}
          </div>
        ) : currentTab === 'map' ? (
          <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden">
            <MaraudersMap />
          </div>
        ) : currentTab === 'history' ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Hall Pass History</h2>
              <button
                onClick={() => setShowTimeframeModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                <Calendar className="w-4 h-4" />
                {timeframe.label}
                <ChevronRight className="w-4 h-4 transform rotate-90" />
              </button>
            </div>

            <div className="relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-2 w-full border rounded-md mb-2"
              />
            </div>

            {showTimeframeModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl shadow-lg p-6 w-96">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Select Time Frame</h3>
                    <button
                      onClick={() => setShowTimeframeModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-2">
                    {[
                      { type: 'today', label: 'Today' },
                      { type: 'yesterday', label: 'Yesterday' },
                      { type: 'last7', label: 'Last 7 Days' },
                      { type: 'last30', label: 'Last 30 Days' },
                      { type: 'custom', label: 'Custom Range' }
                    ].map((option) => (
                      <button
                        key={option.type}
                        onClick={() => {
                          if (option.type === 'custom') {
                            setShowCustomRange(true);
                            setTimeframe(tf => ({ ...tf, type: 'custom', label: 'Custom Range' }));
                            return;
                          }
                          setTimeframe({
                            type: option.type as any,
                            label: option.label,
                            startDate: new Date(),
                            startTime: '00:00',
                            endDate: new Date(),
                            endTime: '23:59'
                          } as any);
                          applyHistoryFilter({ range: option.type as any, start: null, end: null });
                          setShowTimeframeModal(false);
                        }}
                        className={`flex items-center w-full p-3 rounded-lg ${
                          timeframe.type === option.type ? 'bg-indigo-50 text-indigo-600' : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <span className="flex-1 text-left">{option.label}</span>
                        {timeframe.type === option.type && (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>

                  {showCustomRange && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                        <div className="flex gap-2">
                          <input
                            type="date"
                            className="flex-1 px-3 py-2 border rounded-lg"
                            value={timeframe.startDate.toISOString().split('T')[0]}
                            onChange={(e) => setTimeframe({ ...timeframe, startDate: new Date(e.target.value) })}
                          />
                          <input
                            type="time"
                            className="w-24 px-3 py-2 border rounded-lg"
                            value={timeframe.startTime}
                            onChange={(e) => setTimeframe({ ...timeframe, startTime: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                        <div className="flex gap-2">
                          <input
                            type="date"
                            className="flex-1 px-3 py-2 border rounded-lg"
                            value={timeframe.endDate.toISOString().split('T')[0]}
                            onChange={(e) => setTimeframe({ ...timeframe, endDate: new Date(e.target.value) })}
                          />
                          <input
                            type="time"
                            className="w-24 px-3 py-2 border rounded-lg"
                            value={timeframe.endTime}
                            onChange={(e) => setTimeframe({ ...timeframe, endTime: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      onClick={() => setShowTimeframeModal(false)}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        const startParts = timeframe.startTime.split(':');
                        const endParts = timeframe.endTime.split(':');
                        const start = new Date(timeframe.startDate);
                        start.setHours(parseInt(startParts[0] || '0', 10), parseInt(startParts[1] || '0', 10), 0, 0);
                        const end = new Date(timeframe.endDate);
                        end.setHours(parseInt(endParts[0] || '23', 10), parseInt(endParts[1] || '59', 10), 59, 999);
                        applyHistoryFilter({ range: null, start, end });
                        setShowTimeframeModal(false);
                        setShowCustomRange(false);
                      }}
                      className="px-4 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}

            {filteredHistory.map((pass) => {
              const stud = students.find(s => s.userId === pass.studentId);
              const teacherForPass = teachers.find(t => t.userId === pass.teacherId || t.id === pass.teacherId) || pass.issuedBy;
              const startDt = new Date(pass.startTime);
              const endDt = pass.endTime ? new Date(pass.endTime) : undefined;
              const validStart = !isNaN(startDt.getTime());
              const validEnd = endDt && !isNaN(endDt.getTime());
              const startStr = validStart ? startDt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
              const endStr = validEnd ? endDt!.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (pass.hallPassStatusId ? 'In Progress' : '—');

              return (
                <div key={pass.hallPassId ?? pass.id} className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img
                      src={stud?.avatar || ''}
                      alt={stud?.name || pass.studentName || ''}
                      className="w-10 h-10 rounded-full bg-gray-200"
                    />
                    <div>
                      <p className="font-medium hover:text-indigo-600 cursor-pointer" onClick={() => handleStudentHistoryClick(pass.studentId)}>
                        {stud?.name || pass.studentName || 'Unknown Student'}
                      </p>
                      <p className="text-sm text-gray-500">{teacherForPass?.name || 'Teacher Unknown'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <div className="text-right">
                      <p className="font-medium">
                        {(() => {
                          const hallPassType = hallPassTypes.find(type => type.hallPassTypeId === pass.hallPassTypeId);
                          if (hallPassType?.startLocationDetails && hallPassType?.destinationLocationDetails) {
                            return `${hallPassType.startLocationDetails.name} → ${hallPassType.destinationLocationDetails.name}`;
                          }
                          if (pass.startLocation && pass.destinationLocation) {
                            return `${pass.startLocation.name} → ${pass.destinationLocation.name}`;
                          }
                          if (pass.location && pass.location !== 'Hall Pass') {
                            return pass.location;
                          }
                          return hallPassType?.hallPassTypeName || hallPassType?.hallPassTypeDescription || pass.hallPassTypeName || pass.hallPassTypeDescription || 'Hall Pass';
                        })()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {validStart
                          ? startDt.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                          : (pass.createdAt ? new Date(pass.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '')
                        }
                      </p>
                      <p className="text-sm text-gray-500">{startStr} - {endStr}</p>
                    </div>
                    <div className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full">
                      {pass.duration} minutes
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : currentTab === 'types' ? (
          <div className="bg-white rounded-lg">
            <HallPassTypesPage searchTerm="" statusFilter="all" />
          </div>
        ) : currentTab === 'userHallPassTypes' ? (
          <div className="bg-white rounded-lg">
            <UserHallPassTypesPage />
          </div>
        ) : null}

        {currentTab === 'incidents' && (
          <div className="space-y-4">
            {!showAddIncident && (
              <div className="relative max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-2 w-full border rounded-md mb-2"
                />
              </div>
            )}

            {showAddIncident ? (
              <div className="space-y-2 p-3 border rounded-md bg-white/60">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search students..."
                    value={incidentForm.studentName}
                    onChange={e => { setIncidentForm({ ...incidentForm, studentName: e.target.value }); setShowIncidentSuggestions(true); }}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                  {showIncidentSuggestions && incidentForm.studentName && (
                    <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto z-50">
                      {students.filter(s => s.name.toLowerCase().includes(incidentForm.studentName.toLowerCase())).map(s => (
                        <button
                          key={s.userId}
                          onClick={() => { setIncidentForm({ ...incidentForm, studentName: s.name }); setShowIncidentSuggestions(false); }}
                          className="flex items-center gap-3 w-full p-2 hover:bg-gray-50 text-left"
                        >
                          <img src={s.avatar} alt="" className="w-8 h-8 rounded-full" />
                          <div>
                            <p className="font-medium">{s.name}</p>
                            <p className="text-sm text-gray-500">Grade {s.grade}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <input
                  type="text"
                  placeholder="Incident details"
                  value={incidentForm.incident}
                  onChange={e => setIncidentForm({ ...incidentForm, incident: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />

                <div className="flex gap-2">
                  <button onClick={handleIncidentSave} className="px-3 py-1 bg-indigo-500 text-white rounded">Save</button>
                  <button onClick={() => { setShowAddIncident(false); setIncidentForm({ studentName: '', incident: '' }); }} className="px-3 py-1 bg-gray-200 rounded">Cancel</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAddIncident(true)} className="mb-2 px-2 py-1 bg-indigo-500 text-white rounded flex items-center gap-1">
                <Plus className="w-4 h-4" />Add Incident
              </button>
            )}

            {filteredIncidents.map((inc, idx) => {
              const stud = students.find(s => s.userId === inc.userId);
              const when = inc.createdAt ? new Date(inc.createdAt).toLocaleString() : '';
              return (
                <div key={idx} className="p-2 border rounded-md bg-white/60">
                  <div className="font-medium">{stud?.name || 'Unknown'}</div>
                  <div className="text-sm text-gray-500">
                    {inc.incident}
                    {when && (<span className="ml-2 text-xs text-gray-400">{when}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {currentTab === 'students' && (
          <div className="space-y-2">
            <div className="relative max-w-sm mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 pr-3 py-2 w-full border rounded-md"
              />
            </div>
            {filteredLookupStudents.map(s => (
              <div key={s.userId} className="space-y-2">
                <button
                  onClick={() => setSelectedStudentDetail(s)}
                  className="p-2 border rounded-md bg-white/60 flex items-center gap-2 w-full text-left hover:bg-gray-50"
                >
                  <img src={s.avatar} alt="" className="w-8 h-8 rounded-full" />
                  <span>{s.name}</span>
                </button>
                {selectedStudentDetail?.userId === s.userId && (
                  <div className="p-3 border rounded-md bg-white/70">
                    <div className="flex items-center gap-3">
                      <img src={s.avatar} alt="" className="w-10 h-10 rounded-full" />
                      <div>
                        <p className="font-medium">{s.name}</p>
                        {s.grade && <p className="text-sm text-gray-500">Grade {s.grade}</p>}
                      </div>
                    </div>
                    <div className="mt-2">
                      <h4 className="font-semibold text-sm mb-1">Incidents</h4>
                      {incidents.filter(i => i.userId === s.userId).length === 0 ? (
                        <p className="text-sm text-gray-500">No incidents</p>
                      ) : (
                        incidents
                          .filter(i => i.userId === s.userId)
                          .map((i, idx) => {
                            const when = i.createdAt ? new Date(i.createdAt).toLocaleString() : '';
                            return (
                              <div key={idx} className="text-sm text-gray-700 border-t first:border-t-0 py-1">
                                {i.incident}
                                {when && <span className="ml-2 text-xs text-gray-400">{when}</span>}
                              </div>
                            );
                          })
                      )}
                      {showStudentIncidentForm && selectedStudentDetail?.userId === s.userId ? (
                        <div className="mt-2 space-y-2">
                          <input
                            type="text"
                            className="w-full border rounded px-2 py-1"
                            placeholder="Incident details"
                            value={studentIncidentText}
                            onChange={e => setStudentIncidentText(e.target.value)}
                          />
                          <div className="flex gap-2">
                            <button onClick={handleStudentIncidentSave} className="px-3 py-1 bg-indigo-500 text-white rounded">Save</button>
                            <button onClick={() => { setShowStudentIncidentForm(false); setStudentIncidentText(''); }} className="px-3 py-1 bg-gray-200 rounded">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        selectedStudentDetail?.userId === s.userId && (
                          <button
                            onClick={() => setShowStudentIncidentForm(true)}
                            className="mt-2 text-sm text-indigo-600 underline"
                          >
                            Add Incident
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function HallPasses() {
  return <HallPassView />;
}
